import { defineStore } from 'pinia'
import { store } from '../index'
import { useCache } from '@/hooks/web/useCache'
import type { UserInfo, SummaryTableData, UserRole } from '@/types'

const { wsCache } = useCache()

interface AppState {
  fixedMenu: boolean
  role: UserRole
  userInfo: UserInfo | Record<string, never>
  clickStatus: boolean
  summaryTable: SummaryTableData | Record<string, never>
  managerClassTable: string
  isInitialized: boolean
}

export const useAppStore = defineStore('app', {
  state: (): AppState => {
    return {
      fixedMenu: false, // 是否固定菜单
      role: '', // 角色
      userInfo: {}, // 登录信息存储字段
      clickStatus: false, // 是否点击
      summaryTable: {},
      managerClassTable: '',
      isInitialized: false, // 标记是否已初始化
    }
  },
  getters: {
    getFixedMenu(): boolean {
      return this.fixedMenu
    },
    getRole(): UserRole {
      return this.role
    },
    getUserInfo(): UserInfo | Record<string, never> {
      return this.userInfo
    },
    getClickStatus(): boolean {
      return this.clickStatus
    },
    getSummaryTable(): SummaryTableData | Record<string, never> {
      return this.summaryTable
    },
    getManagerClassTable(): string {
      return this.managerClassTable
    }
  },
  actions: {
    // 初始化store数据
    async initializeStore() {
      if (this.isInitialized) return
      
      try {
        const [fixedMenu, role, userInfo, summaryTable, managerClassTable] = await Promise.all([
          wsCache.get('fixedMenu'),
          wsCache.get('role'),
          wsCache.get('userInfo'),
          wsCache.get('summaryTable'),
          wsCache.get('managerClassTable')
        ])

        this.fixedMenu = fixedMenu || false
        this.role = role || ''
        this.userInfo = userInfo || {}
        this.summaryTable = summaryTable || {}
        this.managerClassTable = managerClassTable || ''
        this.isInitialized = true
      } catch (error) {
        console.error('Failed to initialize store:', error)
        this.isInitialized = true // Still mark as initialized to prevent infinite waiting
      }
    },
    
    async setFixedMenu(fixedMenu: boolean) {
      await wsCache.set('fixedMenu', fixedMenu)
      this.fixedMenu = fixedMenu
    },
    async setRole(role: UserRole) {
      await wsCache.set('role', role)
      this.role = role
    },
    async setUserInfo(userInfo: UserInfo) {
      await wsCache.set('userInfo', userInfo)
      this.userInfo = userInfo
    },
    setClickStatus(clickStatus: boolean) {
      this.clickStatus = clickStatus
    },
    async setSummaryTable(summaryTable: SummaryTableData) {
      await wsCache.set('summaryTable', summaryTable)
      this.summaryTable = summaryTable
    },
    async setManagerClassTable(managerClassTable: string) {
      await wsCache.set('managerClassTable', managerClassTable)
      this.managerClassTable = managerClassTable
    },
    
    // 清除所有数据
    async clearStore() {
      // 清除記憶體狀態
      this.fixedMenu = false
      this.role = ''
      this.userInfo = {}
      this.clickStatus = false
      this.summaryTable = {}
      this.managerClassTable = ''
      this.isInitialized = false

      // 同時清除 Ionic Storage 中的持久化數據，避免舊 token 導致無限重導向
      await Promise.all([
        wsCache.remove('fixedMenu'),
        wsCache.remove('role'),
        wsCache.remove('userInfo'),
        wsCache.remove('summaryTable'),
        wsCache.remove('managerClassTable')
      ])
    }
  }
})

export const useAppStoreWithOut = () => {
  return useAppStore(store)
}